import { BaseSecondSidebarPageContent } from "../../shared/components/base/page/BaseSecondSidebarPageContent";
import { IConnectRender } from "../../shared/components/definitions/IConnectRender";
import { MonitoreoSesionMapRender } from "../../shared/components/modulos/monitoreo/sesion/map/MonitoreoSesionMapRender";
import { MonitoreoSesionPlagasBarRender } from "../../shared/components/modulos/monitoreo/sesion/map/MonitoreoSesionPlagasBarRender";
import { MonitoreoSesionSecondSidebarRender } from "../../shared/components/modulos/monitoreo/sesion/sidebar/MonitoreoSesionSecondSidebarRender";
import { serviceProvider } from "../../shared/comun/provider/ServiceProvider";
import { MonitoreoPlagaEnfermedadItem } from "../../shared/generated/grpc/catalogos_monitoreo_pb";
import { cargarTablaPorCultivoAsync } from "../../shared/modulos/catalogos/monitoreo/services/MonitoreoCultivoPlagaGrpcCatalogService";
import { monitoreoPlagaEnfermedadGrpcCatalogService } from "../../shared/modulos/catalogos/monitoreo/services/MonitoreoPlagaEnfermedadGrpcCatalogService";
import {
  DatosMonitoreo,
  getDatosDeRecorrido,
} from "../../shared/modulos/monitoreo/sesion/agruparPorSemana";
import {
  contarEstadosEvaluados,
  SeleccionPlagas,
} from "../../shared/modulos/monitoreo/sesion/semaforo";
import { fx_createLoteGrpcService } from "../../shared/modulos/terceros/services/LoteGrpcService";
import { fx_createMonitoreoRelacionFincaCultivoPlagaGrpcService } from "../../shared/modulos/configuracion/monitoreo/services/MonitoreoRelacionFincaCultivoPlagaGrpcService";
import { setSeleccionReporte } from "../../shared/modulos/monitoreo/sesion/seleccionReportePendiente";
import { SemanaMonitoreoItem } from "../../shared/modulos/monitoreo/sesion/services/SesionSemanaGroupListByFincaIdService";
import { APP_RUTA_FINCA } from "../components/ClienteAsideMenuRender";
import {
  APP_RUTA_MONITOREO_ANALISIS,
  APP_RUTA_MONITOREO_REPORTE,
} from "../components/ClienteModulesComponent";

/** Página de la vista de sesión de monitoreo de plagas: segundo sidebar con las
 *  semanas de la finca + mapa con los lotes coloreados según el semáforo de la
 *  plaga activa.
 *
 *  Coordina tres piezas que comparten estado pero no se conocen entre sí: el
 *  sidebar elige la **semana**, la barra superior elige **qué plagas** se miran
 *  —todas, un grupo del catálogo o una sola—, y el mapa pinta la intersección
 *  de ambas. */
export class MonitoreoSesionPage extends BaseSecondSidebarPageContent {
  /** El mapa (contenido principal). */
  protected getMainRender(): IConnectRender {
    this._mapRender = new MonitoreoSesionMapRender();

    // La barra de plagas se monta dentro del panel superior del mapa, que es
    // el hueco que `ConfigTopActions.onMountEnd` expone para esto.
    this._plagasBar = new MonitoreoSesionPlagasBarRender((seleccion) =>
      this._onSeleccionPlagasChange(seleccion),
    );
    this._mapRender.setOnTopActionsMount((element) =>
      this._plagasBar?.mount(element),
    );

    void this._loadCatalogoAsync();
    void this._loadLotesAsync();
    return this._mapRender;
  }

  /** Sidebar de semanas: volver → mapa de finca; seleccionar → pinta esa
   *  semana. */
  protected getSecondSidebarRender(): IConnectRender {
    this._sidebarRender = new MonitoreoSesionSecondSidebarRender(
      () => serviceProvider.getLayoutService().navigate(APP_RUTA_FINCA),
      (semana, sesionId) => this._onSeleccionChange(semana, sesionId),
      () => this._seleccionPlagas,
      () => this._plagasById,
      () => this._onVerAnalisis(),
      (semana, sesionId) => this._onVerInforme(semana, sesionId),
    );
    return this._sidebarRender;
  }

  dispose(): void {
    this._plagasBar?.dispose();
    this._plagasBar = null;
    this._mapRender = null;
    this._sidebarRender = null;
    super.dispose();
  }

  /** Mapa vivo. */
  private _mapRender: MonitoreoSesionMapRender | null = null;
  /** Barra de plagas del panel superior. */
  private _plagasBar: MonitoreoSesionPlagasBarRender | null = null;
  /** Sidebar vivo. El nombre lleva sufijo porque `BaseSecondSidebarPageContent`
   *  ya tiene un `_sidebar` privado propio. */
  private _sidebarRender: MonitoreoSesionSecondSidebarRender | null = null;

  /** Plagas activas: las que colorean el mapa y las que muestra el sidebar.
   *  `null` = modo "Todas"; una lista puede ser una plaga o un grupo entero. */
  private _seleccionPlagas: SeleccionPlagas = null;
  /** Catálogo de plagas indexado por id. */
  private _plagasById = new Map<string, MonitoreoPlagaEnfermedadItem>();
  /** Incidencias de la selección activa (semana completa o un recorrido). */
  private _datos: DatosMonitoreo | null = null;
  /** Ids de todos los lotes de la finca, para el conteo de la leyenda. */
  private _loteIds: string[] = [];

  /** Carga el catálogo de plagas y se lo pasa al mapa y a la barra. Sale de
   *  IndexedDB salvo la primera vez (`BaseGetCatalogService`). */
  private async _loadCatalogoAsync(): Promise<void> {
    try {
      const finca = serviceProvider.getFincaSelectedService().getRequired();
      // El catálogo global trae una fila por plaga Y POR CULTIVO (80 filas para
      // 44 nombres: "Mosca blanca" existe para aguacate, cítricos, guanábana,
      // macadamia, mango y papa). Mostrarlo entero repetía la misma plaga varias
      // veces en la barra y llenaba la pantalla con plagas de cultivos que esta
      // finca no tiene. Se acota a las que están configuradas para ella.
      const [catalogo, configuradas] = await Promise.all([
        monitoreoPlagaEnfermedadGrpcCatalogService.getListAsync(),
        fx_createMonitoreoRelacionFincaCultivoPlagaGrpcService(finca.id)
          .getListAsync()
          .catch(() => []),
        // Va al final y sin desestructurar: deja lista la tabla por cultivo para
        // el motor de evaluación, no devuelve nada y nunca falla hacia arriba.
        cargarTablaPorCultivoAsync(),
      ]);
      const idsDeLaFinca = new Set(configuradas.map((r) => r.plagaEnfermedadId));
      const deLaFinca = catalogo.filter((plaga) => idsDeLaFinca.has(plaga.id));
      // Se cae al catálogo completo si el filtro no deja NADA -no solo si no hubo
      // configuración-. El cruce puede quedar vacío aunque ambas listas tengan
      // datos: los catálogos viven en IndexedDB con sincronización delta, así que
      // si la base de abajo cambia, los ids cacheados dejan de calzar y la
      // intersección da cero. Una barra larga es recuperable; una vacía deja el
      // mapa sin poder colorearse y parece que la pantalla se rompió.
      const plagas = deLaFinca.length > 0 ? deLaFinca : catalogo;
      this._plagasById = new Map(plagas.map((plaga) => [plaga.id, plaga]));
      this._mapRender?.setPlagas(plagas);
      this._plagasBar?.setPlagas(plagas);
      // El sidebar pudo alcanzar a pintar sus filas sin catálogo (sin umbrales
      // no hay estado que mostrar): ahora ya se pueden resolver. Lo mismo el
      // conteo — sin catálogo todos los lotes caían en "sin monitorear".
      this._sidebarRender?.refreshItems();
      this._refreshConteo();
    } catch {
      serviceProvider
        .getErrorService()
        .addError(
          "Monitoreo de plagas",
          "No se pudo cargar el catálogo de plagas.",
        );
    }
  }

  /** El sidebar cambió de semana o de recorrido: mapa y barra muestran lo
   *  mismo. Con `sesionId` valen solo los lotes de esa fecha; sin él, la
   *  semana completa. */
  private _onSeleccionChange(
    semana: SemanaMonitoreoItem,
    sesionId: string | null,
  ): void {
    this._datos = getDatosDeRecorrido(semana, sesionId);
    this._mapRender?.setDatos(this._datos);
    // La barra recalcula qué plagas se ofrecen y, si la activa ya no aplica,
    // elige otra y avisa por su callback — que a su vez refresca el conteo.
    this._plagasBar?.setDatos(this._datos);
    this._refreshConteo();
  }

  /** La barra cambió de selección —una plaga, un grupo o "Todas"—: la pintan el
   *  mapa **y** el sidebar.
   *
   *  `null` no es "ninguna" sino el modo "Todas": cada lote toma su peor estado.
   *  El sidebar se repinta acá porque sus filas muestran valores de la selección
   *  activa; sin esto quedaba mostrando los de la anterior mientras el mapa ya
   *  pintaba la nueva. */
  private _onSeleccionPlagasChange(seleccion: SeleccionPlagas): void {
    this._seleccionPlagas = seleccion;
    this._mapRender?.setSeleccion(seleccion);
    this._sidebarRender?.refreshItems();
    this._refreshConteo();
  }

  /** Recalcula el resumen de la leyenda: cuántos lotes hay de cada color.
   *
   *  Va sobre **todos los lotes de la finca**, no solo los monitoreados: de ahí
   *  sale "sin monitorear", que es la resta entre el listado de lotes y los que
   *  aparecen en `datos`. Y sigue a la plaga activa, no a "Todas" fijo, porque
   *  tiene que contar exactamente lo que el mapa está pintando.
   *
   *  Se llama desde los tres puntos que cambian lo mostrado —semana/fecha,
   *  plaga y llegada del catálogo—; sin filtros propios: mientras no haya
   *  umbrales, `contarEstados` devuelve `null` y la leyenda queda en blanco. */
  private _refreshConteo(): void {
    if (!this._sidebarRender) return;
    this._sidebarRender.setConteo(
      contarEstadosEvaluados(
        this._loteIds,
        this._datos,
        this._seleccionPlagas,
        this._plagasById,
      ),
    );
  }

  /** Ids de todos los lotes de la finca. Sale del **mismo servicio cacheado
   *  que ya usa el mapa** para dibujar los polígonos, así que no agrega una
   *  petición: en la segunda visita ni siquiera toca la red. */
  private async _loadLotesAsync(): Promise<void> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    try {
      const lotes =
        await fx_createLoteGrpcService(finca.id).getListAsync();
      this._loteIds = lotes.map((lote) => lote.id);
      this._refreshConteo();
    } catch {
      // Sin listado no hay conteo, pero el mapa y el sidebar siguen andando:
      // la leyenda se queda sin números en vez de romper la vista.
    }
  }

  /** Botón del pie del sidebar: lleva al dashboard histórico de la finca. */
  private _onVerAnalisis(): void {
    serviceProvider.getLayoutService().navigate(APP_RUTA_MONITOREO_ANALISIS);
  }

  /** Botón de informe de una semana: lleva a la pantalla del reporte ya puesta
   *  en lo que se estaba viendo — la semana entera, o el día elegido.
   *
   *  La selección viaja por `setSeleccionReporte` y no por la ruta porque
   *  `navigate()` solo acepta una ruta: sin eso, la pantalla del informe
   *  aterrizaría siempre en la semana más reciente y el "informe del día" sería
   *  mentira.
   *
   *  Va la **fecha** y no el año: la pantalla del informe arma su selector con
   *  el año del calendario, que en la semana que cruza diciembre no coincide
   *  con el año ISO de esta semana. */
  private _onVerInforme(
    semana: SemanaMonitoreoItem,
    sesionId: string | null,
  ): void {
    const sesion = semana.sesiones.find((item) => item.id === sesionId);
    setSeleccionReporte({
      fecha: sesion?.fecha ?? semana.fecha,
      semana: semana.semana,
      sesionId,
    });
    serviceProvider.getLayoutService().navigate(APP_RUTA_MONITOREO_REPORTE);
  }
}
